import {
  Modal,
  Button,
  Group,
  Stack,
  Select,
  Checkbox,
  Text,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useCallback, useMemo, useState } from "react";
import { ErrorReportFilters } from "../../services/workflows.service";
import { normalizeDateOnlyQueryRange } from "../../services/dateOnlyQuery";
import {
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";

const CATEGORY_OPTIONS = [
  { value: "accessioning", label: "Accessioning" },
  { value: "grossing", label: "Grossing" },
  { value: "processing", label: "Processing" },
];

interface ErrorReportFilterModalProps {
  opened: boolean;
  onClose: () => void;
  filters: ErrorReportFilters;
  onApply: (filters: ErrorReportFilters) => void;
}

export default function ErrorReportFilterModal({
  opened,
  onClose,
  filters,
  onApply,
}: ErrorReportFilterModalProps) {
  const [tempFilters, setTempFilters] = useState<ErrorReportFilters>(filters);
  const [fromDate, setFromDate] = useState<DateOnlyString | null>(
    parseDateOnly(filters.fromDate) ?? null
  );
  const [toDate, setToDate] = useState<DateOnlyString | null>(
    parseDateOnly(filters.toDate) ?? null
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const labToday = useMemo(() => getLabTodayDateOnly(), []);

  const handleModalOpened = useCallback(() => {
    const nextFromDate = parseDateOnly(filters.fromDate) ?? null;
    const nextToDate = parseDateOnly(filters.toDate) ?? null;
    setTempFilters(filters);
    setFromDate(nextFromDate);
    setToDate(nextToDate);
    if (
      (filters.fromDate && !nextFromDate) ||
      (filters.toDate && !nextToDate)
    ) {
      setDateRangeError("Select valid calendar dates.");
    } else {
      setDateRangeError(null);
    }
  }, [filters]);

  const handleApply = useCallback(() => {
    if (dateRangeError) return;
    try {
      const dateRange = normalizeDateOnlyQueryRange(
        { from: fromDate ?? undefined, to: toDate ?? undefined },
        { from: "From Date", to: "To Date" }
      );
      onApply({
        ...tempFilters,
        fromDate: dateRange.from,
        toDate: dateRange.to,
      });
      setDateRangeError(null);
      onClose();
    } catch {
      setDateRangeError("From Date must be before or equal to To Date.");
    }
  }, [dateRangeError, fromDate, onApply, onClose, tempFilters, toDate]);

  const handleClearAll = useCallback(() => {
    setTempFilters({});
    setFromDate(null);
    setToDate(null);
    setDateRangeError(null);
  }, []);

  const handleCategoryChange = useCallback((categories: string[]) => {
    setTempFilters((current) => ({
      ...current,
      categories: categories.length > 0 ? categories : undefined,
    }));
  }, []);

  const handleBlockingChange = useCallback((value: string | null) => {
    setTempFilters((current) => ({
      ...current,
      blocking: value as "blocking" | "non-blocking" | undefined,
    }));
  }, []);

  const handleFromDateChange = useCallback((value: unknown) => {
    if (value === null) {
      setFromDate(null);
      setDateRangeError(null);
      return;
    }
    const parsed = parseDateOnly(value);
    if (!parsed) {
      setDateRangeError("Select a valid From Date.");
      return;
    }
    setFromDate(parsed);
    setDateRangeError(null);
  }, []);

  const handleToDateChange = useCallback((value: unknown) => {
    if (value === null) {
      setToDate(null);
      setDateRangeError(null);
      return;
    }
    const parsed = parseDateOnly(value);
    if (!parsed) {
      setDateRangeError("Select a valid To Date.");
      return;
    }
    setToDate(parsed);
    setDateRangeError(null);
  }, []);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Filter Error Reports"
      size="lg"
      centered
      closeOnClickOutside={false}
      onEnterTransitionEnd={handleModalOpened}
    >
      <Stack gap="lg">
        {/* Category Filter - Multi-select checkboxes */}
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Category
          </Text>
          <Checkbox.Group
            value={tempFilters.categories ?? []}
            onChange={handleCategoryChange}
          >
            <Group>
              {CATEGORY_OPTIONS.map((cat) => (
                <Checkbox key={cat.value} value={cat.value} label={cat.label} />
              ))}
            </Group>
          </Checkbox.Group>
        </Stack>

        {/* Blocking Filter */}
        <Select
          label="Blocking Status"
          placeholder="All"
          value={tempFilters.blocking || null}
          onChange={handleBlockingChange}
          data={[
            { value: "blocking", label: "Blocking Only" },
            { value: "non-blocking", label: "Non-blocking Only" },
          ]}
          clearable
        />

        {/* Date Range */}
        <Group grow>
          <DateInput
            label="From Date"
            placeholder="Select date"
            value={fromDate}
            onChange={handleFromDateChange}
            maxDate={toDate ?? labToday}
            clearable
          />
          <DateInput
            label="To Date"
            placeholder="Select date"
            value={toDate}
            onChange={handleToDateChange}
            minDate={fromDate ?? undefined}
            maxDate={labToday}
            error={dateRangeError}
            clearable
          />
        </Group>

        {/* Action buttons */}
        <Group justify="space-between" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Group gap="sm">
            <Button variant="subtle" onClick={handleClearAll}>
              Clear All
            </Button>
            <Button onClick={handleApply}>Apply Filters</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
